import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from 'react-data-table-component';

import { buscarGastos } from '../../actions/gastos';
import { buscarSucursales } from '../../actions/sucursales';
import { buscarProveedores } from '../../actions/proveedores';
import { buscarMetodosTipo } from '../../actions/metodos';
import { buscarEventos } from '../../actions/eventos';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { formatNumber } from '../../utils/generalFunctions';

import NuevoGasto from './NuevoGasto';
import NuevoGastoMultiple from './NuevoGastoMultiple';
import EditarGasto from './EditarGasto';
import EliminarGasto from './EliminarGasto';
import FiltrosGasto from './FiltrosGasto';

const filtrosVacios = {
    sucursal: null,
    proveedor: null,
    metodo: null,
    evento: null,
    fecha_inicio: null,
    fecha_fin: null,
    status: null,
    adeudado: null,
    remobolsado: null
};

const Gastos = () => {

    const dispatch = useDispatch();

    const { listado } = useSelector(state => state.gastos);
    const { usuario } = useSelector(state => state.auth);

    const [nuevoModal, setNuevoModal] = useState(false);
    const [nuevoMultipleModal, setNuevoMultipleModal] = useState(false);
    const [gastoSeleccionado, setGastoSeleccionado] = useState(null);
    const [gastoEliminar, setGastoEliminar] = useState(null);
    const [filtrosModal, setFiltrosModal] = useState(false);
    const [filtros, setFiltros] = useState(filtrosVacios);

    useEffect(() => {
        dispatch(buscarGastos(filtrosVacios));
        dispatch(buscarSucursales());
        dispatch(buscarProveedores());
        dispatch(buscarMetodosTipo(5));
        dispatch(buscarEventos(false));
        dispatch(editarBreadcrumb([
            { nombre: 'Gastos', path: '/gastos', activo: true }
        ]));
    }, []);

    const handleAplicarFiltros = (nuevosFiltros) => {
        setFiltros(nuevosFiltros);
        dispatch(buscarGastos(nuevosFiltros));
    };

    const handleEditar = (gasto) => {
        setGastoSeleccionado(gasto);
    };

    const handleEliminar = (gasto) => {
        setGastoEliminar(gasto);
    };

    const handleCerrarEditar = () => {
        setGastoSeleccionado(null);
    };

    const handleCerrarEliminar = () => {
        setGastoEliminar(null);
    };

    const filtrosActivos = [
        filtros.sucursal,
        filtros.proveedor,
        filtros.metodo,
        filtros.evento,
        filtros.fecha_inicio,
        filtros.fecha_fin,
        filtros.status,
        filtros.adeudado,
        filtros.remobolsado
    ].filter(v => v !== null && v !== undefined).length;

    const columns = [
        {
            name: 'Sucursal',
            sortable: true,
            selector: row => row.sucursal?.nombre || '-',
            grow: 1.5, 
            wrap: true
        },
        {
            name: 'Proveedor',
            sortable: true,
            selector: row => row.proveedor?.nombre || '-',
            grow: 1.5, 
            wrap: true
        },
        {
            name: 'Método',
            sortable: true,
            selector: row => row.metodo?.nombre || '-', 
            wrap: true
        },
        {
            name: 'Concepto',
            sortable: true,
            selector: row => row.concepto || '-',
            grow: 3, 
            wrap: true
        },
        {
            name: 'Evento',
            sortable: true,
            selector: row => row.evento?.nombre || '-',
            grow: 1, 
            wrap: true
        },
        {
            name: 'Fecha',
            sortable: true,
            grow: 1.5,
            wrap: true, 
            selector: row => row.fecha
                ? new Date(row.fecha).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                : '-'
        },
        {
            name: 'Total',
            sortable: true,
            wrap: true, 
            selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-'
        },
        {
            name: 'Status',
            sortable: true,
            wrap: true, 
            cell: row => row.status != null
                ? <span className={`badge ${row.status ? 'bg-success' : 'bg-warning text-dark'}`}>{row.status ? 'Pagado' : 'Pendiente'}</span>
                : <span className="text-muted">-</span>
        },
        {
            name: 'A quien se debe',
            sortable: true,
            wrap: true, 
            selector: row => row.adeudado === 0 ? 'Daniel' : row.adeudado === 1 ? 'Socio' : '-'
        },
        {
            name: 'Reembolso',
            sortable: true,
            wrap: true, 
            cell: row => row.remobolsado != null
                ? <span className={`badge ${row.remobolsado === 1 ? 'bg-success' : 'bg-danger'}`}>{row.remobolsado === 1 ? 'Pagado c/ venta' : 'Aún no'}</span>
                : <span className="text-muted">-</span>
        },
        {
            name: '',
            cell: row => (
                <div>
                    {usuario?.permisos?.gastos?.editar && (
                        <button
                            className="btn btn-sm btn-warning me-1"
                            title="Editar"
                            onClick={() => handleEditar(row)}
                        >
                            <i className="fas fa-edit"></i>
                        </button>
                    )}
                    {usuario?.permisos?.gastos?.eliminar && (
                        <button
                            className="btn btn-sm btn-danger"
                            title="Eliminar"
                            onClick={() => handleEliminar(row)}
                        >
                            <i className="fas fa-trash"></i>
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="row mt-5">
            <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
                <h4 className="mb-4">Gastos</h4>
                <div className="d-flex gap-3">
                    <button className="btn btn-secondary position-relative" onClick={() => setFiltrosModal(true)}>
                        <i className="fas fa-filter me-1"></i> Filtros
                        {filtrosActivos > 0 && (
                            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary">
                                {filtrosActivos}
                            </span>
                        )}
                    </button>
                    {usuario?.permisos?.gastos?.agregar && (
                        <>                            
                            <button className="btn btn-primary" onClick={() => setNuevoModal(true)}>
                                <i className="fas fa-plus me-1"></i> Nuevo gasto
                            </button>
                            <button className="btn btn-primary" onClick={() => setNuevoMultipleModal(true)}>
                                <i className="fas fa-layer-group me-1"></i> Nuevo gasto múltiple
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="col-12">
                <DataTable
                    columns={columns}
                    data={listado}
                    pagination
                    paginationPerPage={20}
                    paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                    highlightOnHover
                    noDataComponent="No hay gastos registrados"
                />
            </div>

            <FiltrosGasto
                show={filtrosModal}
                handleCerrar={() => setFiltrosModal(false)}
                filtros={filtros}
                handleAplicar={handleAplicarFiltros}
            />

            <NuevoGasto
                show={nuevoModal}
                handleCerrar={() => setNuevoModal(false)}
            />

            <NuevoGastoMultiple
                show={nuevoMultipleModal}
                handleCerrar={() => setNuevoMultipleModal(false)}
                onSuccess={() => dispatch(buscarGastos(filtros))}
            />

            <EditarGasto
                gasto={gastoSeleccionado}
                handleCerrar={handleCerrarEditar}
            />

            <EliminarGasto
                gasto={gastoEliminar}
                handleCerrar={handleCerrarEliminar}
            />
        </div>
    );
};

export default Gastos;
